import { useEffect } from 'react';
import cm from 'classnames';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { AspectRatioStory } from 'services/story.service';

import { selectSbClipPlaybackParams } from 'components/app/_helpers/_app.helper';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useSbPlayerContext } from './_mediator/sb-player.provider';
import { getPlayerTime } from './_mediator/sb-player.helper';
import { resetSbPlayback } from './_mediator/sb-player.slice';
import { usePlayerTimestamp } from './lib/use-player-timestamp';
import { usePlayerActive } from './lib/use-player-active';

import { PlayerSeek } from 'components/app/app-components/player-seek/player-seek';
import { TsSlider } from 'components/app/editor/timestamp/ts-slider';
import { SbPlayerElement } from './sb-player-element';
import styles from './_sb-player.module.scss';

export const SbPlayer = () => {
  const dragger = useAppSelector((state) => state.app.dragger);
  const storyLoaded = useAppSelector((state) => state.app.storyLoaded);
  const isAudioPlaying = useAppSelector((state) => state.audio.isAudioPlaying);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const sbWsPlayback = useAppSelector((state) => state.sb.sbWsPlayback);
  const markers = useAppSelector((state) => state.editor.markers);
  const cursorCell = useAppSelector((state) => state.editor__controls.sbPosition.cell);
  const config = useAppSelector((state) => state.editor.storyConfig);
  const { sbPlayback, sbClipPlaybackMap, playerSeekEnabled } = useAppSelector((state) => state.sb);
  const { playerRootElement, faderBg, faderText, sbPlayerSeekTo } = useSbPlayerContext();
  const { playbackClips, activePlayerIndex } = useAppSelector((state) => state.sb__player);
  const { wsGetCursorTime } = useAudioTrackContext();
  const dispatch = useAppDispatch();

  const player = usePlayerActive();
  const isVisible = usePlayerTimestamp();

  useEffect(() => {
    return () => {
      dispatch(resetSbPlayback());
    };
  }, []);

  useEffect(() => {
    if (playerSeekEnabled) return;
    if (!activeClip || !markers) return;
    const playbackParams = selectSbClipPlaybackParams(activeClip, sbClipPlaybackMap);
    sbPlayerSeekTo(activePlayerIndex, getPlayerTime(wsGetCursorTime(), playbackParams, markers, cursorCell));
  }, [playerSeekEnabled]);

  return (
    <div className={styles.root} ref={playerRootElement}>
      <div className={cm(styles.buffering, { [styles['buffering--active']]: sbPlayback && !isAudioPlaying })}>
        Buffering...
      </div>
      <div className={styles.fader}>
        <div ref={faderBg} className={styles.fader__bg} />
        <div ref={faderText} className={styles.fader__text} />
      </div>
      <div className={styles.overlap} />
      {sbWsPlayback ? (
        <div className={styles.onlyAudio}>
          <h3> Audio track playback only</h3>
        </div>
      ) : (
        storyLoaded &&
        playbackClips.map((element, i) => <SbPlayerElement key={i} index={i} playbackElement={element} />)
      )}
      <div
        className={cm(styles.ARframe, {
          [styles.is9x16]: config?.aspectRatio === AspectRatioStory.is9x16,
          [styles.is1x1]: config?.aspectRatio === AspectRatioStory.is1x1,
        })}
      />
      <PlayerSeek
        className={cm(styles.seek, { [styles['seek--hidden']]: dragger !== EnumDragger.cell })}
        player={player}
      />
      <TsSlider isVisible={isVisible} />
    </div>
  );
};
